import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, ChevronRight, HelpCircle
} from 'lucide-react';
import UserDropdown from '../features/auth/UserDropdown';
import { ThemeToggle } from '../../contexts/ThemeContext';

interface Breadcrumb {
  label: string;
  path?: string;
}

interface GlobalNavigationProps {
  title: string;
  breadcrumbs?: Breadcrumb[];
  showBackButton?: boolean;
  onBack?: () => void;
  confirmBeforeNavigate?: boolean;
  navigationMessage?: string;
}

export const GlobalNavigation: React.FC<GlobalNavigationProps> = ({
  title,
  breadcrumbs = [],
  showBackButton = true,
  onBack,
  confirmBeforeNavigate = false,
  navigationMessage = 'You have unsaved changes. Are you sure you want to leave?'
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Handle ESC key to go back
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showBackButton) {
        if (confirmBeforeNavigate) {
          const confirmed = window.confirm(navigationMessage);
          if (!confirmed) return;
        }
        
        if (onBack) {
          onBack();
        } else {
          navigate('/');
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, showBackButton, onBack, confirmBeforeNavigate, navigationMessage]);

  const handleBackClick = () => {
    if (confirmBeforeNavigate) {
      const confirmed = window.confirm(navigationMessage);
      if (!confirmed) return;
    }
    
    if (onBack) {
      onBack();
    } else {
      navigate('/');
    }
  };

  // Generate breadcrumbs based on current location if not provided
  const generateBreadcrumbs = (): Breadcrumb[] => {
    if (breadcrumbs.length > 0) return breadcrumbs;

    const paths = location.pathname.split('/').filter(p => p);
    const generated: Breadcrumb[] = [{ label: 'Home', path: '/' }];

    paths.forEach((path, index) => {
      const fullPath = '/' + paths.slice(0, index + 1).join('/');
      let label = path.charAt(0).toUpperCase() + path.slice(1);
      
      // Special cases for better labels
      switch (path) {
        case 'admin':
          label = 'Admin Dashboard';
          break;
        case 'settings':
          label = 'User Settings';
          break;
        case 'contract-review':
          label = 'Contract Review';
          break;
      }

      generated.push({ label, path: fullPath });
    });

    return generated;
  };

  const displayBreadcrumbs = generateBreadcrumbs();

  return (
    <header className="header backdrop-blur-md bg-surface-overlay border-b border-gray-200 dark:border-gray-800 shadow-sm sticky top-0 z-40">
      <div className="px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left Section - Back Button and Breadcrumbs */}
          <div className="flex items-center flex-1">
            {showBackButton && (
              <button
                onClick={handleBackClick}
                className="mr-4 group flex items-center gap-2 px-3 py-2 rounded-xl
                  hover:bg-gray-100 dark:hover:bg-gray-800
                  transition-all duration-fast"
                title="Back to Main Window (ESC)"
              >
                <ArrowLeft className="w-5 h-5 text-text-secondary group-hover:text-text-primary group-hover:-translate-x-0.5 transition-all" />
                <span className="text-sm font-medium text-text-secondary group-hover:text-text-primary hidden sm:inline">
                  Back to Main
                </span>
              </button>
            )}

            {/* Elegant Breadcrumbs */}
            <nav className="breadcrumbs flex items-center space-x-1 text-sm mb-0">
              {displayBreadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <ChevronRight className="breadcrumb-separator w-4 h-4 text-text-tertiary mx-1" />
                  )}
                  {crumb.path && index < displayBreadcrumbs.length - 1 ? (
                    <button
                      onClick={() => navigate(crumb.path!)}
                      className="breadcrumb-item text-text-secondary hover:text-primary-600 dark:hover:text-primary-400
                        hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 rounded-lg transition-all duration-fast"
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="breadcrumb-current text-text-primary font-medium px-2 py-1
                      bg-gray-100 dark:bg-gray-800 rounded-lg">
                      {crumb.label}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

          {/* Center Section - Elegant Page Title */}
          <div className="hidden md:block">
            <h1 className="font-display text-xl font-semibold text-text-primary tracking-tight">
              {title}
            </h1>
          </div>

          {/* Right Section - Modern Actions */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-fast group"
              title="Help & Support"
              onClick={() => navigate('/settings?tab=help')}
            >
              <HelpCircle className="w-5 h-5 text-text-secondary group-hover:text-primary-500 transition-colors" />
            </button>
            <UserDropdown />
          </div>
        </div>

        {/* Mobile Title */}
        <div className="md:hidden pb-4">
          <h1 className="font-display text-xl font-semibold text-text-primary tracking-tight">
            {title}
          </h1>
        </div>
      </div>

    </header>
  );
}; 